import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';

import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import MainLayout from '../layouts/main';

import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: 'presentation-de-l-agence', element: <PresentationDeLagence /> },
        { path: 'mot-du-ministre', element: <MotDuMinistre /> },
        { path: 'mot-du-dga', element: <MotDuDga /> },
        { path: 'charte-de-l-investiseur', element: <CharteDeLInvestisseur /> },
        { path: 'documents', element: <Documents /> },
        { path: 'actualites', element: <Actualites /> },
        { path: 'communique', element: <Communique /> },
        { path: 'archives-evenements', element: <ArchivesEvenements /> },
        // { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'evenement', element: <Evenement /> },
        { path: 'foire-aux-question', element: <Faqs /> },
        {
          path: '/pourquoi-le-togo',
          children: [
            { path: 'le-togo-en-bref', element: <LeTogoEnBref /> },
            { path: 'vivre-au-togo', element: <VivreAuTogo /> },
            { path: 'travailler-au-togo', element: <TravaillerAuTogo /> },
            { path: 'sites-touristiques', element: <SitesTouristiques /> },
            // { path: 'statistiques-chiffres-cle', element: <StatistiquesEtChiffres /> },
            { path: 'galerie-photo', element: <GaleriePhoto /> },
          ],
        },
        {
          path: '/investir-au-togo',
          children: [
            { path: '10-raisons-d-investir-au-togo', element: <DisRaisonDInvestirAuTogo /> },
            { path: 'le-guide-d-investisseur', element: <GuideDelInvestisseur /> },
            { path: 'profil-secteur', element: <ProfileDuSecteur /> },
          ],
        },
        {
          path: '/zone-franche',
          children: [
            { index: true, element: <PresentationDeLaZoneFranche /> },
            { path: 'conditions-deligibilite', element: <ConditionDEgibilite /> },
            { path: 'avantages-accordes', element: <Avantage /> },
            { path: 'procedure-d-obtention-d-agrement', element: <Procedure /> },
            { path: 'societes-en-activite', element: <SocieteEnActivite /> },
            { path: 'staistiques', element: <Statistique /> },
          ],
        },
        {
          path: '/code-des-investisseurs',
          children: [
            { index: true, element: <PresentationDuCodeDesInvestisseurs /> },
            { path: 'conditions-d-iligibilite', element: <CodeConditionDEgibilite /> },
            { path: 'secteurs-iligible', element: <CodeSecteurDEgibilite /> },
            { path: 'avantages-accordes', element: <CodeAvantage /> },
            { path: 'procedure-d-obtention-d-agrement', element: <CodeProcedure /> },
            { path: 'entreprises-en-activite', element: <CodeEntrepriseEnActivite /> },
            { path: 'staistiques', element: <CodeStatistique /> },
          ],
        },
        {
          path: '/guichet-unique',
          children: [
            { path: 'guichet-unique-de-l-investissement', element: <GuideDeLinvest /> },
            { path: 'aprecu-d-une-procedure-documentee', element: <ApercuProcess /> },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// ACCUEIL
const HomePage = Loadable(lazy(() => import('../pages/home/Home')));
const PresentationDeLagence = Loadable(lazy(() => import('../pages/home/PresentationDeLagence')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const MotDuMinistre = Loadable(lazy(() => import('../pages/home/MotDuMinistre')));
const MotDuDga = Loadable(lazy(() => import('../pages/home/MotDuDga')));
const CharteDeLInvestisseur = Loadable(lazy(() => import('../pages/home/CharteDeLInvestisseur')));
const Documents = Loadable(lazy(() => import('../pages/home/Documents')));
const Actualites = Loadable(lazy(() => import('../pages/home/Actualites')));
const Communique = Loadable(lazy(() => import('../pages/home/Communique')));
const ArchivesEvenements = Loadable(lazy(() => import('../pages/ArchivesEvenements')));

// POURQUOI LE TOGO
const LeTogoEnBref = Loadable(lazy(() => import('../pages/pourquoi_le_Togo/LeTogoEnBref')));
const VivreAuTogo = Loadable(lazy(() => import('../pages/pourquoi_le_Togo/VivreAuTogo')));
const TravaillerAuTogo = Loadable(lazy(() => import('../pages/pourquoi_le_Togo/TravaillerAuTogo')));
const SitesTouristiques = Loadable(lazy(() => import('../pages/pourquoi_le_Togo/SitesTouristiques')));
// const StatistiquesEtChiffres = Loadable(lazy(() => import('../pages/pourquoi_le_Togo/StatistiquesEtChiffres')));
const GaleriePhoto = Loadable(lazy(() => import('../pages/pourquoi_le_Togo/GaleriePhoto')));

// 10 RAISON D'INVESIR AU TOGO
const DisRaisonDInvestirAuTogo = Loadable(lazy(() => import('../pages/investir_au_togo/DixRaisonDInvestirAuTogo')));
const GuideDelInvestisseur = Loadable(lazy(() => import('../pages/investir_au_togo/GuideDelInvestisseur')));
const ProfileDuSecteur = Loadable(lazy(() => import('../pages/investir_au_togo/ProfileDuSecteur')));

// ZONE FRANCHE
const PresentationDeLaZoneFranche = Loadable(lazy(() => import('../pages/zone_franche/PresentationDeLaZoneFranche')));
const ConditionDEgibilite = Loadable(lazy(() => import('../pages/zone_franche/ConditionDEgibilite')));
const Avantage = Loadable(lazy(() => import('../pages/zone_franche/Avantage')));
const Procedure = Loadable(lazy(() => import('../pages/zone_franche/Procedure')));
const SocieteEnActivite = Loadable(lazy(() => import('../pages/zone_franche/SocieteEnActivite')));
const Statistique = Loadable(lazy(() => import('../pages/zone_franche/Statistique')));

// CODE DES INVESTISSEURS
const PresentationDuCodeDesInvestisseurs = Loadable(
  lazy(() => import('../pages/code_des_investissement/PresentationDuCodeDesInvestisseurs'))
);
const CodeConditionDEgibilite = Loadable(
  lazy(() => import('../pages/code_des_investissement/CodeConditionDEgibilite'))
);
const CodeSecteurDEgibilite = Loadable(lazy(() => import('../pages/code_des_investissement/CodeSecteurDEgibilite')));
const CodeAvantage = Loadable(lazy(() => import('../pages/code_des_investissement/CodeAvantage')));
const CodeProcedure = Loadable(lazy(() => import('../pages/code_des_investissement/CodeProcedure')));
const CodeEntrepriseEnActivite = Loadable(
  lazy(() => import('../pages/code_des_investissement/CodeEntrepriseEnActivite'))
);
const CodeStatistique = Loadable(lazy(() => import('../pages/code_des_investissement/CodeStatistique')));

const GuideDeLinvest = Loadable(lazy(() => import('../pages/guideDeLinv/GuideDeLinvest')));
const Evenement = Loadable(lazy(() => import('../pages/Evenement')));

const ApercuProcess = Loadable(lazy(() => import('../pages/guideDeLinv/ApercuDesProcess')));
